import { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import { FriendAccessLevel, ACCESS_LEVEL_LABELS } from '../types/friends';
import FriendRequestModal from '../components/FriendRequestModal';
import AccessLevelChangeModal from '../components/AccessLevelChangeModal';
import AcceptFriendRequestModal from '../components/AcceptFriendRequestModal';
import RemoveFriendModal from '../components/RemoveFriendModal';
import SetFriendUsername from '../components/SetFriendUsername';
import FriendsFeed from '../components/FriendsFeed';
import { FriendsSkeleton } from '../components/LoadingSkeleton';
import { hasFriendUsername } from '../services/friendLookupService';
import {
  UserPlus,
  Users,
  Settings,
  Trash2,
  CheckCircle,
  XCircle,
  Rss,
  Info,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatFriendDisplayName, getFriendInitial } from '../utils/friendDisplayUtils';

type TabType = 'feed' | 'friends-list';

function friendDisplayName(request: { fromUsername?: string; fromEmail?: string; fromDisplayName?: string }): string {
  return formatFriendDisplayName(request);
}

function recipientDisplayName(request: { toUsername?: string; toEmail?: string }): string {
  return formatFriendDisplayName(request);
}

function friendListDisplay(friend: { friendUsername?: string; friendDisplayName?: string; friendEmail: string }): string {
  return formatFriendDisplayName(friend);
}

const Friends = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = (searchParams.get('tab') || 'feed') as TabType;
  const { currentUser } = useAuth();
  const {
    friends,
    friendRequests,
    sentFriendRequests,
    loading,
    sendFriendRequest,
    cancelFriendRequest,
    respondToFriendRequest,
    updateFriendAccessLevel,
    removeFriend,
  } = useFriends();

  const [usernameCheckDone, setUsernameCheckDone] = useState(false);
  const [hasUsername, setHasUsername] = useState(false);
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [showAccessLevelModal, setShowAccessLevelModal] = useState(false);
  const [showAcceptRequestModal, setShowAcceptRequestModal] = useState(false);
  const [showRemoveFriendModal, setShowRemoveFriendModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{
    requestId: string;
    requesterName: string;
    requesterAccessLevel: FriendAccessLevel;
  } | null>(null);
  const [selectedFriend, setSelectedFriend] = useState<{
    friendId: string;
    name: string;
    accessLevel: FriendAccessLevel;
  } | null>(null);
  const [friendToRemove, setFriendToRemove] = useState<{
    friendId: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (!currentUser?.email) {
      setUsernameCheckDone(true);
      setHasUsername(false);
      return;
    }
    let cancelled = false;
    hasFriendUsername(currentUser.email)
      .then((has) => {
        if (!cancelled) {
          setHasUsername(has);
          setUsernameCheckDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasUsername(false);
          setUsernameCheckDone(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.email]);

  // When navigated from a notification (e.g. friend request), open the accept modal for that request
  useEffect(() => {
    const openRequestId = (location.state as { openRequestId?: string } | null)?.openRequestId;
    if (!openRequestId || loading || !usernameCheckDone || !hasUsername) return;
    const request = friendRequests.find((r) => r.id === openRequestId);
    if (request) {
      setSelectedRequest({
        requestId: request.id,
        requesterName: friendDisplayName(request),
        requesterAccessLevel: request.accessLevel,
      });
      setShowAcceptRequestModal(true);
    }
    // Clear state so we don't re-open modal on refresh or back
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, loading, usernameCheckDone, hasUsername, friendRequests, navigate, location.pathname, location.search]);

  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab });
  };

  const handleSendRequest = async (emailOrUsername: string, accessLevel: FriendAccessLevel) => {
    await sendFriendRequest(emailOrUsername, accessLevel);
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      await cancelFriendRequest(requestId);
    } catch {
      // Error handled by context and toast
    }
  };

  const handleRespondToRequest = async (
    requestId: string,
    accept: boolean,
    accessLevel?: FriendAccessLevel
  ) => {
    if (accept && !accessLevel) {
      const request = friendRequests.find(r => r.id === requestId);
      if (request) {
        setSelectedRequest({
          requestId,
          requesterName: friendDisplayName(request),
          requesterAccessLevel: request.accessLevel,
        });
        setShowAcceptRequestModal(true);
      }
      return;
    }
    await respondToFriendRequest(requestId, accept, accessLevel || 'restricted-access');
  };

  const handleAcceptWithAccessLevel = async (yourAccessLevel: FriendAccessLevel) => {
    if (!selectedRequest) return;
    await respondToFriendRequest(selectedRequest.requestId, true, yourAccessLevel);
    setShowAcceptRequestModal(false);
    setSelectedRequest(null);
  };

  const handleUpdateAccessLevel = async (accessLevel: FriendAccessLevel) => {
    if (!selectedFriend) return;
    await updateFriendAccessLevel(selectedFriend.friendId, accessLevel);
    setShowAccessLevelModal(false);
    setSelectedFriend(null);
  };

  const handleRemoveFriendClick = (friendId: string, friendName: string) => {
    setFriendToRemove({ friendId, name: friendName });
    setShowRemoveFriendModal(true);
  };

  const handleConfirmRemoveFriend = async () => {
    if (!friendToRemove) return;
    try {
      await removeFriend(friendToRemove.friendId);
      setShowRemoveFriendModal(false);
      setFriendToRemove(null);
    } catch {
      // Error handled by context and modal
    }
  };

  if (!usernameCheckDone || loading) {
    return <FriendsSkeleton />;
  }

  if (!hasUsername || !currentUser?.email) {
    return (
      <motion.div
        className="space-y-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3 mb-2">
            <Users className="h-8 w-8 text-secondary-600" />
            Friends
          </h1>
          <p className="text-gray-600">
            Set a username to use the Friends feature and share your cruise calendar.
          </p>
        </div>
        <SetFriendUsername
          uid={currentUser!.uid}
          email={currentUser!.email!}
          onSuccess={() => setHasUsername(true)}
        />
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Users className="h-8 w-8 text-secondary-600" />
            Friends
          </h1>
          <p className="text-gray-600 mt-2">
            {activeTab === 'feed' ? 'View all your friends\' upcoming cruises' : 'Manage your friends and cruise sharing'}
          </p>
        </div>
        <button
          onClick={() => setShowAddFriendModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
        >
          <UserPlus className="h-5 w-5" />
          Add Friend
        </button>
      </div>

      {/* Apple Private Relay Notice Banner for Friends Feature */}
      {currentUser?.email?.includes('privaterelay.appleid.com') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3 text-sm text-blue-900 shadow-sm">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-950">Apple Private Relay Active</p>
            <p className="mt-1 text-blue-800">
              You are using an Apple private relay email. Friends can search for and add you using your <strong>Friend Username</strong> (@username) or your private relay email (<span className="font-medium">{currentUser.email}</span>). You can also add a verified <strong>Contact Email</strong> in your profile settings for friends to find you by your personal email.
            </p>
          </div>
        </div>
      )}

      {/* Incoming Friend Requests - Show on both tabs */}
      {friendRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Pending Requests</h2>
          <div className="space-y-3">
            {friendRequests.map((request) => (
              <div
                key={request.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {friendDisplayName(request)}
                  </p>
                  <p className="text-sm text-gray-600 truncate" title={request.fromEmail ?? request.fromUserId}>
                    {request.fromEmail ?? request.fromUserId}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto justify-end">
                  <button
                    onClick={() => handleRespondToRequest(request.id, true)}
                    className="px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleRespondToRequest(request.id, false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Sent Friend Requests - Show on both tabs */}
      {sentFriendRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Sent Requests</h2>
          <div className="space-y-3">
            {sentFriendRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">
                      {recipientDisplayName(request)}
                    </p>
                    <p className="text-sm text-gray-600 truncate" title={request.toEmail ?? request.toUserId}>
                      {request.toEmail ?? request.toUserId}
                    </p>
                    <span
                      className={`inline-block mt-1 text-xs px-2 py-1 rounded-full ${
                        request.accessLevel === 'full-access'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {ACCESS_LEVEL_LABELS[request.accessLevel]}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-end">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      Pending
                    </span>
                    <button
                      onClick={() => handleCancelRequest(request.id)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium flex items-center gap-2"
                      title="Cancel request"
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('feed')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'feed'
                  ? 'border-secondary-500 text-secondary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Rss className="h-5 w-5" />
              Feed
            </button>
            <button
              onClick={() => setActiveTab('friends-list')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'friends-list'
                  ? 'border-secondary-500 text-secondary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-5 w-5" />
              Friends List
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'feed' ? (
            <FriendsFeed onAddFriend={() => setShowAddFriendModal(true)} />
          ) : (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                My Friends ({friends.length})
              </h2>
              {friends.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-md p-12 text-center"
                >
                  <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg text-gray-600 mb-2">No friends yet</p>
                  <p className="text-sm text-gray-500 mb-6">
                    Add friends to start sharing your cruise calendar!
                  </p>
                  <button
                    onClick={() => setShowAddFriendModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                  >
                    <UserPlus className="h-5 w-5" />
                    Add Your First Friend
                  </button>
                </motion.div>
              ) : (
                <div className="space-y-3">
                  {friends.map((friend) => (
                    <motion.div
                      key={friend.friendId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="h-12 w-12 flex-shrink-0 rounded-full bg-secondary-600 flex items-center justify-center text-white font-semibold text-lg">
                          {getFriendInitial(friend)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900">{friendListDisplay(friend)}</p>
                          <p className="text-sm text-gray-600 truncate" title={friend.friendEmail}>
                            {friend.friendEmail}
                          </p>
                          <span
                            className={`hidden sm:inline-block mt-1 text-xs px-2 py-1 rounded-full ${
                              friend.accessLevel === 'full-access'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {ACCESS_LEVEL_LABELS[friend.accessLevel]}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2 flex-shrink-0 w-full sm:w-auto">
                        <span
                          className={`sm:hidden text-xs px-2 py-1 rounded-full ${
                            friend.accessLevel === 'full-access'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {ACCESS_LEVEL_LABELS[friend.accessLevel]}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedFriend({
                                friendId: friend.friendId,
                                name: friendListDisplay(friend),
                                accessLevel: friend.accessLevel,
                              });
                              setShowAccessLevelModal(true);
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Change access level"
                          >
                            <Settings className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleRemoveFriendClick(friend.friendId, friendListDisplay(friend))}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                            title="Remove friend"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <FriendRequestModal
        isOpen={showAddFriendModal}
        onClose={() => setShowAddFriendModal(false)}
        onSend={handleSendRequest}
      />
      {selectedFriend && (
        <AccessLevelChangeModal
          isOpen={showAccessLevelModal}
          onClose={() => {
            setShowAccessLevelModal(false);
            setSelectedFriend(null);
          }}
          currentAccessLevel={selectedFriend.accessLevel}
          friendName={selectedFriend.name}
          onUpdate={handleUpdateAccessLevel}
        />
      )}
      {selectedRequest && (
        <AcceptFriendRequestModal
          isOpen={showAcceptRequestModal}
          onClose={() => {
            setShowAcceptRequestModal(false);
            setSelectedRequest(null);
          }}
          requesterName={selectedRequest.requesterName}
          requesterAccessLevel={selectedRequest.requesterAccessLevel}
          onAccept={handleAcceptWithAccessLevel}
        />
      )}
      {friendToRemove && (
        <RemoveFriendModal
          isOpen={showRemoveFriendModal}
          onClose={() => {
            setShowRemoveFriendModal(false);
            setFriendToRemove(null);
          }}
          friendName={friendToRemove.name}
          onConfirm={handleConfirmRemoveFriend}
        />
      )}
    </motion.div>
  );
};

export default Friends;

